import { useMemo } from 'react';
import { SORT_KEYS } from '../contexts/inventoryConstants';
import { applyAxisFilters, setSourceFileNames } from '../contexts/inventoryFilterAxes';
import { nameSortKey, isCutChild } from '../components/segments/cutChild';
import { UNITS } from '../contexts/inventoryConstants';

/**
 * useInventoryData — pure data logic for filtering, sorting, grouping.
 *
 * Separated from the UI so it can be tested independently and
 * composed into any view mode (list, grouped, grid).
 *
 * Returns:
 *   filtered     — array after all filters applied
 *   pageSlice    — current page of filtered docs
 *   totalPages   — total page count
 *   typeGroups   — { [typeKey]: doc[] } for grouped view
 *   uniqueTypes  — sorted array of canonical types in the dataset
 */

// ── Type classification (matches server-side Python) ─────────

const TYPE_ICONS = {
  pdf: '📄', word: '📝', excel: '📊', powerpoint: '📽️', text: '📃',
  image: '🖼️', audio: '🎵', video: '🎬', archive: '📦',
  email: '✉️', checklist: '✅', unknown: '❓',
};

// MediaKind icons (Level 1 — deterministic from MIME/extension)
const MEDIA_KIND_ICONS = {
  document:     '📄',
  spreadsheet:  '📊',
  presentation: '📽️',
  image:        '🖼️',
  video:        '🎬',
  audio:        '🎵',
  archive:      '📦',
  data_export:  '📋',
  unknown:      '❓',
};

const TYPE_LABELS = {
  pdf: 'PDF Documents', word: 'Word Documents', excel: 'Spreadsheets',
  powerpoint: 'Presentations', text: 'Text Files', image: 'Images',
  audio: 'Audio Files', video: 'Video Files', archive: 'Archives',
  email: 'Emails', checklist: 'Checklists', unknown: 'Other Files',
};

const TYPE_ORDER = [
  'pdf', 'word', 'excel', 'powerpoint', 'text',
  'image', 'audio', 'video', 'archive', 'email', 'unknown', 'checklist',
];

const DOC_PURPOSE_ICONS = {
  // Financial
  expense_report: '💳', financial_statement: '💰', invoice: '🧾', receipt: '🧾',
  bank_statement: '🏦', financial_record: '💰',
  // Investigation
  police_report: '🚔', incident_report: '🚨', arrest_report: '⚖️',
  supplemental_report: '🚔', detective_report: '🔎', lab_report: '🧪',
  // Statements
  witness_statement: '💬', victim_statement: '💬', defendant_statement: '💬',
  // Evidence
  evidence_log: '📋', chain_of_custody: '🔗', autopsy_report: '🔬',
  // Media
  body_camera_footage: '📹', surveillance_video: '📹', general_photo: '🖼️',
  photo_collection: '📸',
  // Medical
  medical_record: '⚕️', hospital_record: '🏥', ems_report: '🚑',
  // Correspondence / admin
  correspondence: '✉️', email_thread: '✉️', discovery_checklist: '✅',
};

// ── Helpers (exported for use in components) ─────────────────

export function getCanonicalType(doc) {
  if (doc.document_purpose === 'discovery_checklist' || doc.document_type === 'discovery_checklist') {
    return 'checklist';
  }
  return doc.canonical_type || doc.characterization?.content_category || 'unknown';
}

export function getMediaKindIcon(mediaKind) {
  return MEDIA_KIND_ICONS[mediaKind?.toLowerCase()] || null;
}

export function getTypeIcon(canonicalType, documentType) {
  if (documentType) {
    const dt = documentType.toLowerCase();
    if (DOC_PURPOSE_ICONS[dt]) return DOC_PURPOSE_ICONS[dt];
    for (const [key, icon] of Object.entries(DOC_PURPOSE_ICONS)) {
      if (dt.includes(key) || key.includes(dt)) return icon;
    }
  }
  return TYPE_ICONS[canonicalType?.toLowerCase()] || '❓';
}

export function getTypeLabel(typeKey) {
  return TYPE_LABELS[typeKey] || typeKey;
}

export function getTypeOrder() {
  return TYPE_ORDER;
}

// Sentences that say how the file was READ rather than what it SAYS ("Text was
// extracted using Optical Character Recognition (OCR), achieving 100% coverage…",
// "Hierarchical analysis sampled 10/61 chunks.", "This content is a single PDF
// document, not a compilation…"). The refined summary is written to include that
// methodology; reading status has its own surface (Search readiness), so the
// inventory shows the content. Whole sentences are dropped at display time —
// cutting a clause out of one left fragments ("The entire document's The document
// is a letter…") — and frozen / legacy analyses need no regeneration.
const METHOD_SENTENCE = [
  /\b(?:text|content)\b[^.]{0,80}\b(?:was|were|is|been)\b[^.]{0,40}\bextracted\b/i,
  /\banalysis (?:\w+ly )?(?:extracted|utilized|used|employed|reviewed|sampled|processed)\b/i,
  /\bOptical Character Recognition\b|\bOCR\b/,
  /\bhierarchical (?:analysis|processing|sampling)\b|\bsampling (?:method|methodology|strategy|approach|percentage)\b|\bchunks?\b/i,
  /\banalysis pipeline\b|\bcharacterization\b[^.]*\bconfidence\b|\bconfidence\b[^.]*\bcharacterization\b/i,
  /\bgaps or warnings\b|\btext extraction\b|\bcontent extraction\b|\bvisually extracted\b/i,
  /\bnot a compilation\b|\bcompilation of multiple distinct documents\b/i,
  /\b\d+% (?:\w+ )?coverage\b|\b(?:document|content) coverage\b/i,
];

// Split after . ! ? when the next sentence starts with a capital, a digit or a quote.
// A wrong split ("vs. James") is harmless: the kept pieces are joined back with a space.
function splitSentences(s) {
  const out = [];
  const re = /[.!?]\s+(?=[A-Z0-9"“(])/g;
  let last = 0;
  let m;
  while ((m = re.exec(s))) {
    out.push(s.slice(last, m.index + 1));
    last = m.index + m[0].length;
  }
  out.push(s.slice(last));
  return out.filter(Boolean);
}

export function cleanSummary(text) {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  return splitSentences(s).filter((x) => !METHOD_SENTENCE.some((re) => re.test(x))).join(' ');
}

export function getSummary(doc) {
  const a = doc.analysis || {};
  return cleanSummary(a.summary_refined || a.summary_synthesis || a.summary || '');
}

export function getDocTypeDisplay(doc) {
  // Prefer the new document_purpose field; fall back to document_type for legacy data
  const raw = doc.document_purpose || doc.document_type || '';
  if (!raw || raw === 'unclassified' || raw === 'unknown') return '';
  return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function getDocCategory(doc) {
  return doc.document_purpose_group || doc.document_category || '';
}

export function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
  return (bytes / 1073741824).toFixed(1) + ' GB';
}

// When the evidence reached the case. A document cut out of a file carries its
// file's upload time (`uploaded_at`, list API); its own created_at is when it was cut.
export const uploadedAt = (doc) => doc?.uploaded_at || doc?.created_at || null;

export function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return null;
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

export function getMediaDuration(doc) {
  const kind = (doc.media_kind || '').toLowerCase();
  const meta = doc.characterization?.metadata || {};
  if (kind === 'video') {
    const seconds = meta.estimated_duration_seconds;
    if (!seconds) return null;
    return { seconds, isEstimated: !!meta.duration_is_estimated, source: meta.estimation_method || null };
  }
  if (kind === 'audio') {
    const seconds = meta.duration_seconds || meta.estimated_duration_seconds;
    if (!seconds) return null;
    return { seconds, isEstimated: meta.duration_source === 'filesize_estimate', source: meta.duration_source || null };
  }
  return null;
}

// Statuses that mean "still working" — used to tell a not-yet-computed extent
// (pending) apart from one that's genuinely empty or not applicable.
const PROCESSING_STATUSES = new Set(['processing', 'pending', 'queued']);

const TEXT_CHAR_TYPES = new Set(['pdf', 'word', 'excel', 'powerpoint', 'text', 'checklist']);
const NO_TEXT_DOC_TYPES = new Set(['photo_album', 'discovery_checklist']);

/**
 * getDocExtent — the natural "how much content is here" measure for a document,
 * as a *typed* value rather than a pre-joined string. Lets the inventory render
 * it unit-bearing, sort it within a media group, and — crucially — distinguish
 * "not applicable" from "not yet computed" from "genuinely empty".
 *
 * Returns { value, unit, display, title, state }:
 *   state 'value'   → value/unit/display populated (display is unit-bearing)
 *   state 'na'      → this media kind has no extent metric (e.g. images)
 *   state 'pending' → kind has a metric but the doc is still processing
 *   state 'empty'   → processed, but genuinely zero/none
 *
 * The unit travels with the value because the metric is heterogeneous by media
 * kind (pages / seconds / sheets / slides / entries) but homogeneous in meaning.
 */
export function getDocExtent(doc) {
  const kind = (doc.media_kind || '').toLowerCase();
  const charMeta = doc.characterization?.metadata || {};
  const units = doc.extraction?._metadata?.units || {};
  const processing = PROCESSING_STATUSES.has((doc.status || '').toLowerCase());

  const value = (v, unit, display, title = null) => ({ value: v, unit, display, title, state: 'value' });
  const na = (unit) => ({ value: null, unit, display: '—', title: null, state: 'na' });
  const absent = (unit) => processing
    ? { value: null, unit, display: null, title: null, state: 'pending' }
    : { value: null, unit, display: null, title: null, state: 'empty' };

  if (kind === 'video' || kind === 'audio') {
    const dur = getMediaDuration(doc);
    const formatted = dur && formatDuration(dur.seconds);
    if (formatted) {
      const display = dur.isEstimated ? '~' + formatted : formatted;
      const title = dur.isEstimated
        ? (kind === 'audio'
            ? 'Duration estimated from file size — may be inaccurate for non-standard bitrates.'
            : 'Duration estimated from file size — ffprobe was unavailable.')
        : null;
      return value(dur.seconds, 'second', display, title);
    }
    return absent('second');
  }
  if (kind === 'image') {
    // No structured dimensions captured today → not applicable by nature.
    return na('pixel');
  }
  if (kind === 'spreadsheet') {
    const n = charMeta.sheet_count;
    return n ? value(n, 'sheet', n.toLocaleString() + (n === 1 ? ' sheet' : ' sheets')) : absent('sheet');
  }
  if (kind === 'presentation') {
    const n = charMeta.slide_count;
    return n ? value(n, 'slide', n.toLocaleString() + (n === 1 ? ' slide' : ' slides')) : absent('slide');
  }
  if (kind === 'archive') {
    const n = charMeta.file_count;
    return n ? value(n, 'entry', n.toLocaleString() + (n === 1 ? ' entry' : ' entries')) : absent('entry');
  }
  // document, data_export, unknown, or legacy docs missing media_kind → pages.
  // (units.total_units is the legacy paginated fallback; guarded by kind so it
  // can't leak transcript-segment counts into A/V as "1,318 pages".)
  const pc = charMeta.page_count || units.total_units || doc.page_count;
  return pc ? value(pc, 'page', pc.toLocaleString() + (pc === 1 ? ' page' : ' pages')) : absent('page');
}

/** getDocCost — AI processing cost as { cents, display }. display is '—' when none. */
export function getDocCost(doc) {
  const cents = doc.processing_cost_cents || 0;
  if (cents <= 0) return { cents: 0, display: '—' };
  const usd = cents / 100;
  return { cents, display: usd < 0.01 ? '<$0.01' : '$' + usd.toFixed(2) };
}

/** getDocChars — extracted text length as { value, display }, text docs only.
 *  display is '—' for non-text kinds or when no count is recorded. */
export function getDocChars(doc) {
  if (!TEXT_CHAR_TYPES.has(getCanonicalType(doc)) || NO_TEXT_DOC_TYPES.has(doc.document_type)) {
    return { value: null, display: '—' };
  }
  const n = doc.text_char_count;
  if (!n) return { value: null, display: '—' };
  return { value: n, display: n.toLocaleString() + ' chars' };
}

/**
 * getDocStats — compact one-line summary (extent · chars · size · cost),
 * composed from the typed primitives above. Retained for the document detail
 * panel; the inventory list now breaks these out into separate columns.
 */
export function getDocStats(doc) {
  const parts = [];
  const extent = getDocExtent(doc);
  if (extent.state === 'value') parts.push(extent.display);

  const chars = getDocChars(doc);
  if (chars.value) parts.push(chars.display);

  parts.push(formatSize(doc.file_size));

  const cost = getDocCost(doc);
  if (cost.cents > 0) parts.push(cost.display);

  return { text: parts.join(' · '), title: extent.title };
}

// ── Filter logic ─────────────────────────────────────────────

/** Apply folder / search / dupes filters — everything BUT axis chips.
 *  Used both as the input to axis filtering (final list) and as the
 *  doc-set the picker reasons over for contextual facet counts.
 */
function applyNonAxisFilters(docs, { currentFolder, searchText, duplicateFilter, duplicateHashFilter, hashCountMap }) {
  let result = docs;

  // Folder filter
  if (currentFolder === '__unfiled__') {
    result = result.filter(d => !d.folder_path);
  } else if (currentFolder === '__check__') {
    // "Check these": the documents the folders' own rules could not place (the server says why)
    result = result.filter(d => !!d.folder_check);
  } else if (currentFolder && currentFolder !== '__all__') {
    // Match against ALL memberships (home + copies), not just the home
    // folder. A copied doc's home stays elsewhere, so filtering on the
    // singular folder_path would hide it even though it's in this folder.
    result = result.filter(d => {
      const paths = (d.folder_paths && d.folder_paths.length)
        ? d.folder_paths
        : (d.folder_path ? [d.folder_path] : []);
      return paths.some(p => p === currentFolder || p.startsWith(currentFolder + '/'));
    });
  }

  // Duplicate filter (all groups)
  if (duplicateFilter && hashCountMap) {
    result = result.filter(d => {
      const h = d.file_hash_sha256;
      return h && hashCountMap[h] > 1;
    });
  }

  // Duplicate hash filter (single group)
  if (duplicateHashFilter) {
    result = result.filter(d => d.file_hash_sha256 === duplicateHashFilter);
  }

  // Text search
  if (searchText) {
    const q = searchText.toLowerCase();
    result = result.filter(d => {
      const fn = (d.filename || '').toLowerCase();
      const dt = (d.document_purpose || d.document_type || '').toLowerCase();
      const dc = (d.document_purpose_group || d.document_category || '').toLowerCase();
      const sum = getSummary(d).toLowerCase();
      return fn.includes(q) || dt.includes(q) || dc.includes(q) || sum.includes(q);
    });
  }

  return result;
}

function applyFilters(docs, ctx) {
  const docsForFacets = applyNonAxisFilters(docs, ctx);
  // Axis filters — compositional chip rail. See FILTER_AXES registry.
  // AND across axes; OR within an axis (multi-value).
  const filtered = applyAxisFilters(docsForFacets, ctx.filters || [], {
    checklistDocMap: ctx.checklistDocMap,
    hasChecklist: ctx.hasChecklist,
  });
  return { filtered, docsForFacets };
}

// ── Sort logic ───────────────────────────────────────────────

function sortDocs(docs, sortKey, sortAsc) {
  const sorted = [...docs];
  const dir = sortAsc ? 1 : -1;

  sorted.sort((a, b) => {
    switch (sortKey) {
      case SORT_KEYS.FILENAME:
        // extension-less keys: a file cut into documents sorts directly above them, in reading order
        return dir * nameSortKey(a).localeCompare(nameSortKey(b), undefined, { sensitivity: 'base', numeric: true });
      case SORT_KEYS.DATE:
        return dir * ((uploadedAt(a) || '') > (uploadedAt(b) || '') ? 1 : -1);
      case SORT_KEYS.SIZE:
        return dir * ((a.file_size || 0) - (b.file_size || 0));
      case SORT_KEYS.EXTENT:
        // Meaningful within a media group (the inventory groups by type); a
        // flat cross-kind sort compares raw magnitude, which is approximate
        // (pages vs seconds), so docs with no extent sink via value 0.
        return dir * ((getDocExtent(a).value || 0) - (getDocExtent(b).value || 0));
      case SORT_KEYS.COST:
        return dir * ((a.processing_cost_cents || 0) - (b.processing_cost_cents || 0));
      case SORT_KEYS.TYPE:
        return dir * getCanonicalType(a).localeCompare(getCanonicalType(b));
      case SORT_KEYS.STATUS:
        return dir * (a.status || '').localeCompare(b.status || '');
      default:
        return 0;
    }
  });

  return sorted;
}

// ── Grouping logic ───────────────────────────────────────────

function groupByType(docs) {
  const groups = {};
  for (const d of docs) {
    const ct = getCanonicalType(d);
    if (!groups[ct]) groups[ct] = [];
    groups[ct].push(d);
  }
  return groups;
}

// ── Unit scoping (plan §3.4c) ────────────────────────────────
// The unit decides which rows exist at all, BEFORE folder/search/filters, sort,
// counts and pagination — so every number on the screen agrees with the rows.
//   files      → every row that is not a document cut out of a file (plain files,
//                the cut files themselves, zip/email parents).
//   documents  → every row that is not a cut file (the documents cut out of it,
//                plain files, zip/email children); the cut files come back with
//                `showSourceFiles`.
export function scopeToUnit(documents, unit, showSourceFiles = false) {
  if (unit === UNITS.FILES) return documents.filter(d => !isCutChild(d));
  if (showSourceFiles) return documents;
  return documents.filter(d => !d.is_compilation_container);
}

/** Row counts per unit, from the full list — the unit switch shows them. */
export function countUnits(documents) {
  let children = 0; let cutFiles = 0;
  for (const d of documents || []) {
    if (isCutChild(d)) children += 1;
    else if (d.is_compilation_container) cutFiles += 1;
  }
  const total = (documents || []).length;
  return { files: total - children, documents: total - cutFiles, cutFiles, children };
}

// ── Main hook ────────────────────────────────────────────────

export default function useInventoryData(allDocuments, {
  unit = UNITS.DOCUMENTS,
  showSourceFiles = false,
  currentFolder,
  searchText,
  filters,
  duplicateFilter,
  duplicateHashFilter,
  sortKey,
  sortAsc,
  page,
  pageSize,
}, extra = {}) {
  return useMemo(() => {
    const unitCounts = countUnits(allDocuments);
    setSourceFileNames(allDocuments);                 // "Source file" chip labels (hook-less registry)
    const documents = scopeToUnit(allDocuments || [], unit, showSourceFiles);
    if (!documents || documents.length === 0) {
      return {
        filtered: [],
        docsForFacets: [],
        sorted: [],
        pageSlice: [],
        totalPages: 1,
        typeGroups: {},
        uniqueTypes: [],
        uniqueCategories: [],
        hashCountMap: {},
        duplicateGroupCount: 0,
        pendingCorrectionCount: 0,
        hasPendingPropagation: false,
        unitDocs: documents,
        unitCounts,
      };
    }

    // 0. Build hash count map (from full dataset, before filtering)
    const hashCountMap = {};
    for (const d of documents) {
      const h = d.file_hash_sha256;
      if (h) hashCountMap[h] = (hashCountMap[h] || 0) + 1;
    }
    const duplicateGroupCount = Object.values(hashCountMap).filter(c => c > 1).length;
    const correctionDocs = documents.filter(d => d.classification_source === 'human_manual');
    const correctionCount = correctionDocs.length;
    // "Pending" = corrected doc whose latest classification_history entry
    // isn't an "ack". Acked corrections still show the row badge (the
    // doc IS still corrected — factual) but drop out of the toolbar's
    // "M pending" count and don't drive the Propagate button.
    const pendingCorrectionCount = correctionDocs.filter(d => {
      const history = d.classification_history || [];
      if (history.length === 0) return true;
      const latest = history[history.length - 1];
      return latest?.source !== 'ack';
    }).length;

    const lastRunTs = extra.lastPropagationRunAt ? new Date(extra.lastPropagationRunAt).getTime() : null;
    const hasPendingPropagation = pendingCorrectionCount > 0 && (
      lastRunTs === null ||
      documents.some(d => {
        if (d.classification_source !== 'human_manual') return false;
        const history = d.classification_history || [];
        const humanEntry = [...history].reverse().find(e => e.source === 'human_manual');
        if (!humanEntry) return false;
        const acked = [...history].reverse().find(e => e.source === 'ack');
        const ackTs = acked ? new Date(acked.timestamp).getTime() : 0;
        const humanTs = new Date(humanEntry.timestamp).getTime();
        // Only "pending" for propagation if the latest human edit is
        // still un-acked AND newer than the last propagation run.
        return humanTs > ackTs && humanTs > lastRunTs;
      })
    );

    // 1. Filter — applyFilters returns both the final filtered set and
    // docsForFacets (after folder/search/dupes but BEFORE axis chips),
    // which is what the picker reasons over for contextual facet counts.
    const { filtered, docsForFacets } = applyFilters(documents, {
      currentFolder, searchText, filters, duplicateFilter, duplicateHashFilter,
      hashCountMap,
      checklistDocMap: extra.checklistDocMap || {},
      hasChecklist: Object.keys(extra.checklistDocMap || {}).length > 0,
    });

    // 2. Sort
    const sorted = sortDocs(filtered, sortKey, sortAsc);

    // 3. Paginate
    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    const pageSlice = sorted.slice(start, start + pageSize);

    // 4. Group (for grouped view mode)
    const typeGroups = groupByType(sorted);

    // 5. Unique types (for filter dropdown)
    const typeCounts = {};
    for (const d of documents) {
      const ct = getCanonicalType(d);
      typeCounts[ct] = (typeCounts[ct] || 0) + 1;
    }
    const uniqueTypes = TYPE_ORDER.filter(t => typeCounts[t]);
    // Add any types not in TYPE_ORDER
    for (const t of Object.keys(typeCounts)) {
      if (!TYPE_ORDER.includes(t)) {
        uniqueTypes.push(t);
      }
    }

    // 6. Unique categories — prefer purpose_group, fall back to document_category
    const categoryCounts = {};
    for (const d of documents) {
      const cat = d.document_purpose_group || d.document_category || '';
      if (cat) categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
    }
    const uniqueCategories = Object.entries(categoryCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));

    return { filtered, docsForFacets, sorted, pageSlice, totalPages, typeGroups, uniqueTypes, uniqueCategories, hashCountMap, duplicateGroupCount, pendingCorrectionCount, hasPendingPropagation, unitDocs: documents, unitCounts };
  }, [allDocuments, unit, showSourceFiles, currentFolder, searchText, filters, duplicateFilter, duplicateHashFilter, sortKey, sortAsc, page, pageSize, extra]);
}
